import React, {useEffect, useState} from 'react'
import {profilePictureSrc, resolveMediaUrl} from '../../../api-utils/constantVariables'
import {KTIcon, toAbsoluteUrl} from '../../../_metronic/helpers'
import {useNavigate, useParams} from 'react-router-dom'
import {restRequest} from '../../../api-utils/apiQueries'
import Pagination from '../../common-function/Pagination'

const avatarFallback = toAbsoluteUrl('/media/user2.png')

const subscriptionBadge = (category?: string) => {
  if (!category || category === 'none') {
    return <span className='text-muted'>–</span>
  }
  const map: Record<string, {label: string; className: string}> = {
    free_trial_active: {label: 'Free Trial Active', className: 'badge-light-info'},
    subscribed: {label: 'Subscribed', className: 'badge-light-success'},
    inactive_subscription: {label: 'Inactive Subscription', className: 'badge-light-danger'},
  }
  const cfg = map[category]
  if (!cfg) return <span className='text-muted'>–</span>
  return <span className={`badge ${cfg.className}`}>{cfg.label}</span>
}

const tripStatusBadge = (status?: string) => {
  if (!status) return <span className='text-muted'>—</span>
  const map: Record<string, string> = {
    completed: 'badge-light-success',
    cancel: 'badge-light-danger',
    cancelled: 'badge-light-danger',
    not_started: 'badge-light-warning',
  }
  const cls = map[status] ?? 'badge-light-primary'
  return <span className={`badge ${cls}`}>{status}</span>
}

const formatGiftCardSummary = (data: unknown): string => {
  if (!data) return '—'
  if (typeof data === 'string') return data
  if (typeof data === 'object') {
    const o = data as Record<string, unknown>
    if (o.title) return String(o.title)
    if (o.Title) return String(o.Title)
    if (o.name) return String(o.name)
    if (o.ProductName) return String(o.ProductName)
    if (o.productName) return String(o.productName)
    if (o.gift_url) return String(o.gift_url)
    if (o.GiftURL) return String(o.GiftURL)
    return JSON.stringify(data)
  }
  return String(data)
}

const toInt = (value?: number | string | null): number => {
  const n = Number(value ?? 0)
  return Number.isFinite(n) ? Math.round(n) : 0
}

const formatDollarValue = (points?: number | string | null): string => {
  const n = Number(points ?? 0)
  return `$${((Number.isFinite(n) ? n : 0) / 100).toFixed(2)}`
}

const formatClaimedAt = (value?: string | null): string => {
  if (!value) return '—'
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString()
}

const ProfileField = ({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) => (
  <div className='row mb-7'>
    <label className='col-lg-4 fw-bold text-muted'>{label}</label>
    <div className='col-lg-8'>{children}</div>
  </div>
)

const STAT_LABELS: Record<string, string> = {
  registration_fee_annual: 'Registration (annual)',
  insurance: 'Insurance',
  servicing_and_tyre_cost: 'Servicing & tyres',
  road_side_assistance: 'Roadside assistance',
  fuel: 'Fuel',
  parking: 'Parking',
  toll: 'Toll',
  threshold: 'Threshold',
  accepted_request: 'Accepted requests',
  rejected_request: 'Rejected requests',
  confirm_trips_as_driver: 'Confirmed (driver)',
  confirm_trips_as_passenger: 'Confirmed (passenger)',
}

const formatStatLabel = (key: string): string =>
  STAT_LABELS[key] ??
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

const StatMiniCard = ({label, value}: {label: string; value: React.ReactNode}) => (
  <div className='border border-gray-300 border-dashed rounded p-4 h-100'>
    <div className='text-gray-600 fw-semibold fs-7 mb-1'>{label}</div>
    <div className='fw-bold fs-6 text-gray-900 lh-1'>{value ?? '—'}</div>
  </div>
)

const StatsGridCard = ({
  title,
  data,
  className = '',
}: {
  title: string
  data: Record<string, unknown>
  className?: string
}) => (
  <div className={`card card-flush h-100 mb-0 d-flex flex-column ${className}`}>
    <div className='card-header border-0 pt-5 pb-0 min-h-50px'>
      <h3 className='card-title fw-bold text-gray-900 m-0'>{title}</h3>
    </div>
    <div className='card-body pt-5 pb-5'>
      <div className='row g-3'>
        {Object.entries(data).map(([key, value]) => (
          <div className='col-6' key={key}>
            <StatMiniCard label={formatStatLabel(key)} value={value as React.ReactNode} />
          </div>
        ))}
      </div>
    </div>
  </div>
)

const UserDetails = () => {
  const [userData, setUserData] = useState<any>({
    name: '',
    mobile: '',
    email: '',
    profession: '',
    role: '',
    address: '',
    profile_picture: '',
    wallet_balance: 0,
    subscription: null,
    car: null,
    gift_cards_claimed: [],
  })
  const [threshold, setThreshold] = useState({
    registration_fee_annual: 0,
    insurance: 0,
    servicing_and_tyre_cost: 0,
    road_side_assistance: 0,
    fuel: 0,
    parking: 0,
    toll: 0,
    threshold: 0,
  })
  const [allTrips, setAllTrips] = useState({
    accepted_request: 0,
    rejected_request: 0,
    confirm_trips_as_driver: 0,
    confirm_trips_as_passenger: 0,
  })
  const [userTrips, setUserTrips] = useState<any[]>([])
  const [tripsPage, setTripsPage] = useState(1)
  const [tripsTotalPages, setTripsTotalPages] = useState(0)
  const params = useParams()
  const navigate = useNavigate()

  useEffect(() => {
    fetchUserData()
    fetchCarThreshold()
    fetchTrips()
  }, [])

  useEffect(() => {
    if (params.id) fetchUserTripsList()
  }, [params.id, tripsPage])

  const fetchUserData = async () => {
    const response: any = await restRequest.getUserData(params.id)
    setUserData(response.data.data)
  }

  const fetchUserTripsList = async () => {
    const response: any = await restRequest.getUserTripsList(params.id, tripsPage)
    const payload = response?.data?.data
    setUserTrips(payload?.data ?? [])
    setTripsTotalPages(payload?.pager?.totalPages ?? 0)
  }

  const fetchCarThreshold = async () => {
    const response: any = await restRequest.getCarThresholdData(params.id)
    setThreshold(response.data.data)
  }

  const fetchTrips = async () => {
    const response: any = await restRequest.getUserTripData(params.id)
    setAllTrips(response.data.data)
  }

  const profileSrc = profilePictureSrc(userData.profile_picture, avatarFallback)
  const car = userData.car
  const subscription = userData.subscription
  const giftCards: any[] = userData.gift_cards_claimed ?? []
  const pointsInfo = userData.points ?? {}
  const passengerPointsPurchased = toInt(
    pointsInfo.passenger_points_purchased ?? userData.purchased_points
  )
  const driverPointsEarned = toInt(pointsInfo.driver_points_earned ?? userData.earned_points)
  const totalTravelCredit = toInt(
    pointsInfo.total_travel_credit ?? userData.wallet_balance ?? userData.total_points
  )
  const hasThreshold = Object.keys(threshold).length > 0

  const handleImgError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.src = avatarFallback
  }

  return (
    <>
      <div className='d-flex align-items-center gap-3 mb-5'>
        <button
          type='button'
          className='btn btn-sm btn-light-primary'
          onClick={() => navigate('/user-management')}
        >
          ← Back to User Management
        </button>
      </div>

      {/* Overview */}
      <div className='card card-flush mb-5 mb-xl-10'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column m-0'>
            <span className='card-label fw-bold text-gray-900'>Overview</span>
          </h3>
        </div>

        <div className='card-body pt-9 pb-9'>
          <div className='row g-5 g-xl-8 align-items-start'>
            {/* Identity */}
            <div className='col-lg-4 col-xl-3'>
              <div className='d-flex flex-column align-items-center align-items-lg-start text-center text-lg-start'>
                <div className='symbol symbol-100px symbol-circle mb-5'>
                  <img
                    src={profileSrc}
                    alt={userData.name || 'Profile'}
                    className='rounded-circle object-fit-cover'
                    onError={handleImgError}
                  />
                </div>
                <div className='d-flex flex-wrap align-items-center justify-content-center justify-content-lg-start gap-2 mb-2'>
                  <span className='text-gray-900 fs-2 fw-bold'>{userData.name || '—'}</span>
                  {subscription?.category ? subscriptionBadge(subscription.category) : null}
                </div>
                {userData.email ? (
                  <div className='d-flex align-items-center justify-content-center justify-content-lg-start text-gray-500 fw-semibold fs-6 mb-1'>
                    <KTIcon iconName='sms' className='fs-5 me-2 text-gray-400' />
                    {userData.email}
                  </div>
                ) : null}
                {userData.mobile ? (
                  <div className='d-flex align-items-center justify-content-center justify-content-lg-start text-gray-500 fw-semibold fs-6'>
                    <KTIcon iconName='phone' className='fs-5 me-2 text-gray-400' />
                    {userData.mobile}
                  </div>
                ) : null}
              </div>
            </div>

            {/* Wallet + gift cards */}
            <div className='col-lg-8 col-xl-9'>
              <div className='row g-5 h-100'>
                <div className='col-sm-5 col-xl-4'>
                  <div className='bg-light-primary rounded min-h-125px p-5 h-100'>
                    <div className='d-flex align-items-center mb-4'>
                      <KTIcon iconName='wallet' className='fs-2x text-primary me-3 flex-shrink-0' />
                      <span className='fw-bold text-gray-800 fs-6'>Wallet points</span>
                    </div>
                    <div className='d-flex justify-content-between align-items-center mb-2'>
                      <span className='text-gray-600 fw-semibold fs-7'>
                        Passenger Points Purchased
                      </span>
                      <span className='fw-bold fs-6 text-gray-900'>{passengerPointsPurchased}</span>
                    </div>
                    <div className='d-flex justify-content-between align-items-center mb-2'>
                      <span className='text-gray-600 fw-semibold fs-7'>Driver Points Earned</span>
                      <span className='fw-bold fs-6 text-gray-900'>{driverPointsEarned}</span>
                    </div>
                    <div className='separator separator-dashed my-2'></div>
                    <div className='d-flex justify-content-between align-items-center'>
                      <span className='text-gray-700 fw-bold fs-7'>Total Travel Credit</span>
                      <span className='fw-bold fs-5 text-gray-900'>
                        {totalTravelCredit}{' '}
                        <span className='text-muted fw-semibold fs-7'>
                          ({formatDollarValue(totalTravelCredit)})
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                <div className='col-sm-7 col-xl-8'>
                  <div className='border border-gray-300 border-dashed rounded p-5 h-100'>
                    <div className='d-flex align-items-center mb-4'>
                      <KTIcon iconName='gift' className='fs-2 text-gray-500 me-3' />
                      <span className='fw-bold text-gray-800 fs-6'>Gift cards claimed</span>
                      <span className='badge badge-light ms-3'>{giftCards.length}</span>
                    </div>

                    {giftCards.length === 0 ? (
                      <div className='d-flex flex-column align-items-center justify-content-center py-6'>
                        <KTIcon iconName='gift' className='fs-3x text-gray-300 mb-3' />
                        <span className='text-muted fs-7 fw-semibold'>No gift cards claimed</span>
                      </div>
                    ) : (
                      <div className='table-responsive'>
                        <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3 mb-0'>
                          <thead>
                            <tr className='fw-bold text-muted fs-7 text-uppercase'>
                              <th className='min-w-150px ps-0'>Gift card</th>
                              <th className='min-w-120px text-end pe-0'>Claimed at</th>
                            </tr>
                          </thead>
                          <tbody>
                            {giftCards.map((row: any, index: number) => (
                              <tr key={row.id ?? index}>
                                <td className='text-gray-800 fw-semibold fs-7 ps-0'>
                                  {formatGiftCardSummary(row.gift_card_data)}
                                </td>
                                <td className='text-gray-600 fs-7 text-end pe-0'>
                                  {formatClaimedAt(row.createdAt)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Details */}
      <div className='card card-flush mb-5 mb-xl-10' id='kt_profile_details_view'>
        <div className='card-header border-0'>
          <div className='card-title m-0'>
            <h3 className='fw-bold m-0 text-gray-900'>Profile Details</h3>
          </div>
        </div>

        <div className='card-body p-9'>
          <ProfileField label='Full Name'>
            <span className='fw-bold fs-6 text-gray-900'>{userData.name || '—'}</span>
          </ProfileField>

          <ProfileField label='Address'>
            <span className='fw-semibold fs-6 text-gray-800'>{userData.address || '—'}</span>
          </ProfileField>

          <ProfileField label='Phone'>
            <div className='d-flex flex-wrap align-items-center gap-2'>
              <span className='fw-bold fs-6 text-gray-900'>{userData.mobile || '—'}</span>
              {userData.mobile ? <span className='badge badge-light-success'>Verified</span> : null}
            </div>
          </ProfileField>

          <ProfileField label='Email'>
            <span className='fw-semibold fs-6 text-gray-800'>{userData.email || '—'}</span>
          </ProfileField>

          <ProfileField label='Profession'>
            <span className='fw-bold fs-6 text-gray-900'>{userData.profession || '—'}</span>
          </ProfileField>

          <ProfileField label='Subscription'>
            <div>
              {subscriptionBadge(subscription?.category)}
              {subscription?.plan_title ? (
                <span className='ms-3 text-gray-700 fw-semibold fs-6'>
                  {subscription.plan_title}
                </span>
              ) : null}
              {subscription?.subscription_start_date ? (
                <div className='text-muted fs-7 mt-2'>
                  {subscription.subscription_start_date} – {subscription.subscription_end_date}
                </div>
              ) : null}
            </div>
          </ProfileField>
        </div>
      </div>

      {/* Vehicle */}
      {car ? (
        <div className='card card-flush mb-5 mb-xl-10'>
          <div className='card-header border-0'>
            <h3 className='card-title fw-bold m-0 text-gray-900'>Vehicle</h3>
          </div>
          <div className='card-body p-9'>
            <div className='row g-5'>
              <div className='col-lg-4'>
                {car.car_photo ? (
                  <div className='symbol symbol-200px w-100'>
                    <img
                      src={resolveMediaUrl(car.car_photo)}
                      alt='Vehicle'
                      className='rounded object-fit-cover w-100'
                      style={{maxHeight: 200}}
                      onError={handleImgError}
                    />
                  </div>
                ) : (
                  <div className='d-flex flex-column align-items-center justify-content-center bg-light rounded p-8'>
                    <KTIcon iconName='car' className='fs-3x text-gray-400 mb-3' />
                    <span className='text-muted fs-7'>No photo</span>
                  </div>
                )}
              </div>
              <div className='col-lg-8'>
                <ProfileField label='Make / Model'>
                  <span className='fw-bold fs-6 text-gray-900'>
                    {car.car_brand_name} {car.car_model_name}
                  </span>
                </ProfileField>
                <ProfileField label='Registration'>
                  <span className='fw-semibold fs-6 text-gray-800'>
                    {car.car_registration || '—'}
                  </span>
                </ProfileField>
                <ProfileField label='Colour'>
                  <span className='fw-semibold fs-6 text-gray-800'>{car.color || '—'}</span>
                </ProfileField>
                <ProfileField label='Seats'>
                  <span className='fw-semibold fs-6 text-gray-800'>{car.no_of_seats ?? '—'}</span>
                </ProfileField>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Stats row */}
      <div className='row g-5 gx-xl-10 mb-5 mb-xl-10 align-items-stretch'>
        {hasThreshold ? (
          <div className='col-xl-4'>
            <StatsGridCard title='Car Threshold' data={threshold} />
          </div>
        ) : null}

        <div className={`col-xl-4 ${hasThreshold ? '' : 'col-xl-6'}`}>
          <StatsGridCard title='Trips and Trip Requests' data={allTrips} />
        </div>

        <div className={`col-xl-4 ${hasThreshold ? '' : 'col-xl-6'}`}>
          <div className='card card-flush h-100 mb-0 d-flex flex-column'>
            <div className='card-header border-0 pt-5 pb-0 min-h-50px'>
              <h3 className='card-title fw-bold text-gray-900 m-0'>User Trips</h3>
            </div>

            <div className='card-body pt-5 pb-0 d-flex flex-column flex-grow-1 min-h-0'>
              {userTrips.length === 0 ? (
                <div className='d-flex flex-column align-items-center justify-content-center py-6'>
                  <KTIcon iconName='route' className='fs-2x text-gray-300 mb-2' />
                  <span className='text-muted fs-7 fw-semibold'>No trips found</span>
                </div>
              ) : (
                <div
                  className='table-responsive overflow-y-auto'
                  style={{maxHeight: 300}}
                >
                  <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2 mb-0 table-sm'>
                    <thead className='position-sticky top-0 bg-white'>
                      <tr className='fw-bold text-muted fs-8 text-uppercase'>
                        <th className='min-w-40px ps-0 py-2'>ID</th>
                        <th className='py-2'>Status</th>
                        <th className='py-2'>Start</th>
                        <th className='text-end pe-0 py-2'>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userTrips.map((trip: any) => (
                        <tr key={trip.id}>
                          <td className='text-gray-800 fw-bold fs-7 ps-0 py-2'>{trip.id}</td>
                          <td className='py-2'>{tripStatusBadge(trip.trip_status)}</td>
                          <td className='text-gray-700 fs-7 py-2'>{trip.start_date ?? '—'}</td>
                          <td className='text-gray-600 fs-7 text-end pe-0 py-2'>
                            {trip.createdAt
                              ? new Date(trip.createdAt).toLocaleString()
                              : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {tripsTotalPages > 1 ? (
              <div className='card-footer border-0 pt-0 pb-5'>
                <Pagination
                  totalPages={tripsTotalPages}
                  page={tripsPage}
                  setPage={setTripsPage}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>
  )
}

export default UserDetails
